import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ProjectTask, TaskGroup, TaskStatus, TaskPriority, CRMUser } from '../../types';
import GroupHeader from './GroupHeader';
import TaskRow from './TaskRow';
import { Plus } from 'lucide-react';
import type { CommitTaskDatesHint } from '../../utils/taskDateCommit';
interface TaskTableProps {
  tasks: ProjectTask[];
  groups: TaskGroup[];
  teamMembers: CRMUser[];
  projectId: string;
  onUpdateTask: (id: string, updates: Partial<ProjectTask>) => void;
  onCreateTask: (task: Partial<ProjectTask> & { projectId: string; groupId: string }) => void;
  onDeleteTask: (id: string) => void;
  onSelectTask: (task: ProjectTask) => void;
  /** Open the consolidated comments popup for a whole group (Design, Procurement, …). */
  onOpenGroupComments?: (group: TaskGroup) => void;
  /** Whether a group has unread comments (drives the dot on the group comments button). */
  isGroupUnread?: (group: TaskGroup) => boolean;
  onUpdateGroup: (id: string, updates: Partial<TaskGroup>) => void;
  onDeleteGroup: (id: string) => void;
  onCreateGroup: (projectId: string) => void;
  /** Virtual onboarding section ids — cannot be renamed/deleted and cannot receive new tasks */
  readOnlyGroupIds?: string[];
  /** Child tasks keyed by parent id (for subtask count badges). */
  childTasksByParent?: Map<string, ProjectTask[]>;
  /** Hide the trailing "Add Group" button (e.g. supplier hub mode). */
  hideAddGroup?: boolean;
  /** Supplier hub: large labeled Comments button on group headers. */
  prominentGroupComments?: boolean;
  hidePriority?: boolean;
  hideAction?: boolean;
  canOpenGroupComments?: (group: TaskGroup) => boolean;
  datesEditable?: boolean;
  onCommitTaskDates?: (taskId: string, startDate: Date, endDate: Date, hint: CommitTaskDatesHint) => void | Promise<void>;
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks, groups, teamMembers, projectId,
  onUpdateTask, onCreateTask, onDeleteTask, onSelectTask,
  onOpenGroupComments, isGroupUnread,
  onUpdateGroup, onDeleteGroup, onCreateGroup,
  readOnlyGroupIds,
  childTasksByParent,
  hideAddGroup,
  prominentGroupComments = false,
  hidePriority = false,
  hideAction = false,
  canOpenGroupComments,
  datesEditable = true,
  onCommitTaskDates,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [addingTaskGroupId, setAddingTaskGroupId] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [draggedTask, setDraggedTask] = useState<ProjectTask | null>(null);
  /** Tracks HTML5-drag even when dragend hasn’t cleared React state yet. */
  const draggedTaskRef = useRef<ProjectTask | null>(null);
  const tableRootRef = useRef<HTMLDivElement>(null);

  /** While dragging a row, scroll the hub content pane near edges so long tables are reachable. */
  useEffect(() => {
    const EDGE = 70;
    const MAX_STEP = 32;

    const onDragOver = (e: DragEvent) => {
      if (!draggedTaskRef.current) return;
      const root = tableRootRef.current?.closest('.pm-hub-content') as HTMLElement | null;
      if (!root) return;
      const r = root.getBoundingClientRect();
      const { clientX: x, clientY: y } = e;
      const axL = x < r.left + EDGE ? (r.left + EDGE - x) / EDGE : 0;
      const axR = x > r.right - EDGE ? (x - (r.right - EDGE)) / EDGE : 0;
      const ayT = y < r.top + EDGE ? (r.top + EDGE - y) / EDGE : 0;
      const ayB = y > r.bottom - EDGE ? (y - (r.bottom - EDGE)) / EDGE : 0;
      if (axL > 0) {
        const step = Math.min(MAX_STEP, Math.ceil(MAX_STEP * Math.min(1, axL)));
        root.scrollLeft -= step;
      }
      if (axR > 0) {
        const step = Math.min(MAX_STEP, Math.ceil(MAX_STEP * Math.min(1, axR)));
        root.scrollLeft += step;
      }
      if (ayT > 0) {
        const step = Math.min(MAX_STEP, Math.ceil(MAX_STEP * Math.min(1, ayT)));
        root.scrollTop -= step;
      }
      if (ayB > 0) {
        const step = Math.min(MAX_STEP, Math.ceil(MAX_STEP * Math.min(1, ayB)));
        root.scrollTop += step;
      }
    };

    const onDragEnd = () => {
      draggedTaskRef.current = null;
      setDraggedTask(null);
    };

    document.addEventListener('dragover', onDragOver, { passive: true });
    document.addEventListener('dragend', onDragEnd);

    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragend', onDragEnd);
    };
  }, []);

  const tasksByGroup = useMemo(() => {
    const map = new Map<string, ProjectTask[]>();
    for (const g of groups) {
      map.set(g.id!, []);
    }
    const ungrouped: ProjectTask[] = [];
    for (const t of tasks) {
      const gid = t.groupId || '';
      if (map.has(gid)) {
        map.get(gid)!.push(t);
      } else {
        ungrouped.push(t);
      }
    }
    map.forEach(arr => {
      arr.sort((a, b) => (a.position || 0) - (b.position || 0));
    });
    if (ungrouped.length > 0) {
      map.set('__ungrouped', ungrouped);
    }
    return map;
  }, [tasks, groups]);

  const toggleCollapse = useCallback((groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const handleAddTask = (groupId: string) => {
    if (!newTaskTitle.trim()) return;
    const groupTasks = tasksByGroup.get(groupId) || [];
    const maxPos = groupTasks.reduce((max, t) => Math.max(max, t.position || 0), 0);
    onCreateTask({
      projectId,
      groupId,
      title: newTaskTitle.trim(),
      status: 'pending' as TaskStatus,
      priority: 'medium' as TaskPriority,
      assignedTo: [],
      department: 'design',
      dependencies: [],
      progress: 0,
      position: maxPos + 1.0,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    });
    setNewTaskTitle('');
    setAddingTaskGroupId(null);
  };

  const handleToggleComplete = (task: ProjectTask) => {
    const newStatus: TaskStatus = task.status === 'completed' ? 'pending' : 'completed';
    onUpdateTask(task.id!, { status: newStatus, progress: newStatus === 'completed' ? 100 : 0 });
  };

  const handleDragStart = (_e: React.DragEvent, task: ProjectTask) => {
    draggedTaskRef.current = task;
    setDraggedTask(task);
  };

  const handleDrop = (_e: React.DragEvent, targetTask: ProjectTask) => {
    const from = draggedTaskRef.current ?? draggedTask;
    if (!from || from.id === targetTask.id) return;
    const updates: Partial<ProjectTask> = {
      groupId: targetTask.groupId,
      position: targetTask.position - 0.001,
    };
    onUpdateTask(from.id!, updates);
    draggedTaskRef.current = null;
    setDraggedTask(null);
  };

  const handleDropOnGroup = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    const from = draggedTaskRef.current ?? draggedTask;
    if (!from) return;
    const groupTasks = tasksByGroup.get(groupId) || [];
    const maxPos = groupTasks.reduce((max, t) => Math.max(max, t.position || 0), 0);
    onUpdateTask(from.id!, { groupId, position: maxPos + 1.0 });
    draggedTaskRef.current = null;
    setDraggedTask(null);
  };

  /** Order comes from Project Hub (`sortTaskGroupsForProject`); do not re-sort by position. */
  const sortedGroups = groups;

  return (
    <div className="pm-task-table" ref={tableRootRef}>
      {/* Column headers — must mirror the cells in <TaskRow /> 1:1 or the
          labels visually shift one column to the right. */}
      <div className="pm-table-header">
        <div className="pm-th pm-th-grip" />
        <div className="pm-th pm-th-check" />
        <div className="pm-th pm-th-title">Task</div>
        <div className="pm-th pm-th-assignee">Assignee</div>
        <div className="pm-th pm-th-status">Status</div>
        {!hidePriority && <div className="pm-th pm-th-priority">Priority</div>}
        <div className="pm-th pm-th-date">Due Date</div>
        {!hideAction && <div className="pm-th pm-th-action">Action</div>}
        <div className="pm-th pm-th-progress">Progress</div>
      </div>

      {/* Groups */}
      {sortedGroups.map(group => {
        const groupTasks = tasksByGroup.get(group.id!) || [];
        const completedCount = groupTasks.filter(t => t.status === 'completed').length;
        const isCollapsed = collapsedGroups.has(group.id!);
        const isReadOnlyGroup = readOnlyGroupIds?.includes(group.id!);

        return (
          <div
            key={group.id}
            className="pm-table-group"
            onDragOver={e => e.preventDefault()}
            onDrop={e => { if (!isReadOnlyGroup) handleDropOnGroup(e, group.id!); }}
          >
            <GroupHeader
              group={group}
              taskCount={groupTasks.length}
              completedCount={completedCount}
              collapsed={isCollapsed}
              onToggleCollapse={() => toggleCollapse(group.id!)}
              onRename={name => onUpdateGroup(group.id!, { name })}
              onDelete={() => onDeleteGroup(group.id!)}
              readOnly={!!isReadOnlyGroup}
              onOpenComments={
                onOpenGroupComments && (!canOpenGroupComments || canOpenGroupComments(group))
                  ? () => onOpenGroupComments(group)
                  : undefined
              }
              hasUnreadComments={isGroupUnread ? isGroupUnread(group) : false}
              prominentComments={prominentGroupComments}
            />

            {!isCollapsed && (
              <div className="pm-table-rows">
                {groupTasks.map(task => {
                  const children = task.id ? childTasksByParent?.get(task.id) : undefined;
                  const subtaskTotal = children?.length ?? task.subtasks?.length ?? 0;
                  const subtaskDone = children
                    ? children.filter((c) => c.status === 'completed').length
                    : (task.subtasks?.filter((s) => s.completed).length ?? 0);
                  return (
                  <TaskRow
                    key={task.id}
                    task={task}
                    teamMembers={teamMembers}
                    onUpdate={onUpdateTask}
                    onSelect={onSelectTask}
                    onToggleComplete={handleToggleComplete}
                    subtaskTotal={subtaskTotal}
                    subtaskDone={subtaskDone}
                    hidePriority={hidePriority}
                    hideAction={hideAction}
                    datesEditable={datesEditable}
                    isSummaryParent={subtaskTotal > 0}
                    onCommitTaskDates={onCommitTaskDates}
                    draggable
                    onDragStart={handleDragStart}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleDrop}
                  />
                  );
                })}

                {/* Add task row */}
                {!isReadOnlyGroup && addingTaskGroupId === group.id ? (
                  <div className="pm-add-task-row">
                    <div className="pm-add-task-spacer" />
                    <input
                      className="pm-inline-input pm-add-task-input"
                      placeholder="Task name..."
                      value={newTaskTitle}
                      onChange={e => setNewTaskTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAddTask(group.id!);
                        if (e.key === 'Escape') { setNewTaskTitle(''); setAddingTaskGroupId(null); }
                      }}
                      onBlur={() => { if (!newTaskTitle.trim()) { setAddingTaskGroupId(null); } }}
                      autoFocus
                    />
                  </div>
                ) : !isReadOnlyGroup ? (
                  <button
                    className="pm-add-task-btn"
                    onClick={() => { setAddingTaskGroupId(group.id!); setNewTaskTitle(''); }}
                  >
                    <Plus size={14} /> Add Task
                  </button>
                ) : null}
              </div>
            )}
          </div>
        );
      })}

      {/* Ungrouped tasks */}
      {tasksByGroup.has('__ungrouped') && (
        <div className="pm-table-group pm-table-ungrouped">
          <div className="pm-group-header pm-group-ungrouped-header">
            <span className="pm-group-name">Ungrouped</span>
            <span className="pm-group-count">{tasksByGroup.get('__ungrouped')!.length}</span>
          </div>
          <div className="pm-table-rows">
            {tasksByGroup.get('__ungrouped')!.map(task => {
              const children = task.id ? childTasksByParent?.get(task.id) : undefined;
              const subtaskTotal = children?.length ?? task.subtasks?.length ?? 0;
              const subtaskDone = children
                ? children.filter((c) => c.status === 'completed').length
                : (task.subtasks?.filter((s) => s.completed).length ?? 0);
              return (
              <TaskRow
                key={task.id}
                task={task}
                teamMembers={teamMembers}
                onUpdate={onUpdateTask}
                onSelect={onSelectTask}
                onToggleComplete={handleToggleComplete}
                subtaskTotal={subtaskTotal}
                subtaskDone={subtaskDone}
                datesEditable={datesEditable}
                isSummaryParent={subtaskTotal > 0}
                onCommitTaskDates={onCommitTaskDates}
              />
              );
            })}
          </div>
        </div>
      )}

      {/* Add group button */}
      {!hideAddGroup && (
        <button className="pm-add-group-btn" onClick={() => onCreateGroup(projectId)}>
          <Plus size={14} /> Add Group
        </button>
      )}
    </div>
  );
};

export default TaskTable;
